import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, ApiError } from '../lib/api';
import { formatRelativeTime, classNames } from '../lib/utils';
import { DELETABLE_TYPES, RUNNING_STATES, resourceKey, formatCostPerDay, ageHours, isUnattributed, MAX_BULK_DELETE } from '../lib/resourceHelpers';
import {
  Trash2,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  ShieldAlert,
  DollarSign,
} from 'lucide-react';
import type { Resource, DeleteResourceResult } from '../lib/types';

const AGE_THRESHOLDS = [
  { label: 'Older than 4 hours', hours: 4 },
  { label: 'Older than 24 hours', hours: 24 },
  { label: 'Older than 3 days', hours: 72 },
  { label: 'Older than 7 days', hours: 168 },
];

export function CleanupPage() {
  const { idToken: accessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [resources, setResources] = useState<Resource[]>([]);
  const [error, setError] = useState('');
  const [minAgeHours, setMinAgeHours] = useState(24);
  const [accountFilter, setAccountFilter] = useState('');
  const [unattributedOnly, setUnattributedOnly] = useState(false);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteResults, setDeleteResults] = useState<DeleteResourceResult[] | null>(null);

  const fetchCandidates = useCallback(async () => {
    if (!accessToken) return;
    setIsLoading(true);
    setError('');
    try {
      // No dedicated "candidates" endpoint - fetch a generous page of the full inventory (optionally
      // scoped to one account) and apply the age/state/attribution heuristics client-side below.
      const res = await api.resources.list(accessToken, { account_id: accountFilter || undefined, limit: 500 });
      setResources(res.resources);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load resources');
      setResources([]);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, accountFilter]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  useEffect(() => {
    setSelected(new Set());
  }, [resources]);

  const accounts = useMemo(() => [...new Set(resources.map((r) => r.account_id))].sort(), [resources]);

  const candidates = useMemo(() => {
    return resources
      .filter((r) => RUNNING_STATES.has((r.state || '').toLowerCase()))
      .filter((r) => ageHours(r) >= minAgeHours)
      .filter((r) => !unattributedOnly || isUnattributed(r))
      .sort((a, b) => (b.estimated_cost_usd_per_day || 0) - (a.estimated_cost_usd_per_day || 0));
  }, [resources, minAgeHours, unattributedOnly]);

  const totalDailyCost = useMemo(
    () => candidates.reduce((sum, r) => sum + (r.estimated_cost_usd_per_day || 0), 0),
    [candidates],
  );
  const unattributedCount = useMemo(() => candidates.filter(isUnattributed).length, [candidates]);

  const deletableCandidates = useMemo(() => candidates.filter((r) => DELETABLE_TYPES.has(r.resource_type)), [candidates]);
  const allSelected = deletableCandidates.length > 0 && deletableCandidates.every((r) => selected.has(resourceKey(r)));

  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(deletableCandidates.slice(0, MAX_BULK_DELETE).map(resourceKey)));
  };
  const toggleSelectOne = (r: Resource) => {
    setSelected((prev) => {
      const key = resourceKey(r);
      if (prev.has(key)) {
        const next = new Set(prev);
        next.delete(key);
        return next;
      }
      if (prev.size >= MAX_BULK_DELETE) return prev;
      return new Set(prev).add(key);
    });
  };

  const selectedResources = useMemo(() => candidates.filter((r) => selected.has(resourceKey(r))), [candidates, selected]);
  const selectedDailyCost = useMemo(
    () => selectedResources.reduce((sum, r) => sum + (r.estimated_cost_usd_per_day || 0), 0),
    [selectedResources],
  );

  const handleDelete = async () => {
    if (!accessToken || selectedResources.length === 0) return;
    setDeleting(true);
    try {
      const items = selectedResources.map((r) => ({ account_id: r.account_id, resource_id: r.resource_id }));
      const res = await api.resources.deleteMany(accessToken, items);
      setDeleteResults(res.results);
      const deletedKeys = new Set(res.results.filter((r) => r.status === 'deleted').map((r) => resourceKey(r)));
      setResources((prev) => prev.filter((r) => !deletedKeys.has(resourceKey(r))));
      setSelected(new Set());
    } catch (err) {
      setDeleteResults(
        selectedResources.map((r) => ({
          account_id: r.account_id,
          resource_id: r.resource_id,
          status: 'failed' as const,
          detail: err instanceof ApiError ? err.message : 'Delete request failed',
        })),
      );
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="page-cleanup">
      <header className="page-header">
        <h1 className="page-title">Cleanup candidates</h1>
        <div className="page-header-actions">
          {selected.size > 0 && (
            <button className="btn btn-danger" onClick={() => setShowConfirm(true)} disabled={deleting} title={`Up to ${MAX_BULK_DELETE} at a time`}>
              <Trash2 size={18} /> Delete {selected.size} selected
            </button>
          )}
          <button className="btn btn-ghost btn-icon" onClick={fetchCandidates} disabled={isLoading} aria-label="Refresh">
            <RefreshCw size={20} className={isLoading ? 'spin' : ''} />
          </button>
        </div>
      </header>

      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--slate-600)', marginBottom: 'var(--space-4)' }}>
        Resources still running past a chosen age, ranked by estimated cost - the ones most likely
        left over from a finished lab. Independent of vedniq-portal's own cleanup pipeline: this
        reflects what AWS itself reports right now, not what any session record claims.
      </p>

      {deleteResults && (
        <div
          className="login-alert"
          style={{
            marginBottom: 'var(--space-4)',
            flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--space-1)',
            background: deleteResults.every((r) => r.status === 'deleted') ? 'var(--color-primary-light)' : undefined,
            borderColor: deleteResults.every((r) => r.status === 'deleted') ? 'var(--color-primary)' : undefined,
            color: deleteResults.every((r) => r.status === 'deleted') ? 'var(--color-primary-dark)' : undefined,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', width: '100%' }}>
            {deleteResults.every((r) => r.status === 'deleted') ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            <strong>
              {deleteResults.filter((r) => r.status === 'deleted').length} deleted
              {deleteResults.some((r) => r.status === 'failed') && `, ${deleteResults.filter((r) => r.status === 'failed').length} failed`}
            </strong>
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setDeleteResults(null)} aria-label="Dismiss" style={{ marginLeft: 'auto' }}>
              <X size={14} />
            </button>
          </div>
          {deleteResults.filter((r) => r.status === 'failed').map((r) => (
            <p key={resourceKey(r)} style={{ fontSize: 'var(--text-xs)' }}>{r.resource_id}: {r.detail}</p>
          ))}
        </div>
      )}

      {error && (
        <div className="login-alert" style={{ marginBottom: 'var(--space-4)' }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
        <div className="card">
          <div className="card-content" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <ShieldAlert size={28} style={{ color: 'var(--color-amber-dark)' }} />
            <div>
              <p style={{ fontSize: 'var(--text-xl)', fontWeight: 800 }}>{candidates.length}</p>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--slate-500)' }}>candidates</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-content" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <DollarSign size={28} style={{ color: 'var(--color-red-dark)' }} />
            <div>
              <p style={{ fontSize: 'var(--text-xl)', fontWeight: 800 }}>${totalDailyCost.toFixed(2)}/day</p>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--slate-500)' }}>estimated running cost</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-content" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <AlertCircle size={28} style={{ color: 'var(--clr-teal)' }} />
            <div>
              <p style={{ fontSize: 'var(--text-xl)', fontWeight: 800 }}>{unattributedCount}</p>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--slate-500)' }}>missing owner/session tags</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="card-content" style={{ padding: 'var(--space-4)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ minWidth: 200, marginBottom: 0 }}>
            <label htmlFor="age-threshold" className="form-label">Minimum age</label>
            <select id="age-threshold" className="form-select" value={minAgeHours} onChange={(e) => setMinAgeHours(Number(e.target.value))}>
              {AGE_THRESHOLDS.map((t) => (
                <option key={t.hours} value={t.hours}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ minWidth: 200, marginBottom: 0 }}>
            <label htmlFor="account-filter" className="form-label">Account</label>
            <select id="account-filter" className="form-select" value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)}>
              <option value="">All accounts</option>
              {accounts.map((acc) => (
                <option key={acc} value={acc}>{acc}</option>
              ))}
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer', paddingBottom: 8 }}>
            <input type="checkbox" checked={unattributedOnly} onChange={(e) => setUnattributedOnly(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--color-primary)' }} />
            <span style={{ fontSize: 'var(--text-sm)' }}>Missing owner/session tags only</span>
          </label>
        </div>
      </div>

      <div className="card">
        {isLoading && resources.length === 0 ? (
          <div className="card-content empty-state">
            <Loader2 size={48} className="empty-state-icon spin" />
            <h3 className="empty-state-title">Scanning inventory...</h3>
          </div>
        ) : candidates.length === 0 ? (
          <div className="card-content empty-state">
            <CheckCircle size={64} className="empty-state-icon" style={{ color: 'var(--color-primary)' }} />
            <h3 className="empty-state-title">Nothing to clean up</h3>
            <p className="empty-state-text">No running resources match these filters right now.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input type="checkbox" aria-label="Select all" checked={allSelected} onChange={toggleSelectAll} disabled={deletableCandidates.length === 0} />
                  </th>
                  <th>Resource</th>
                  <th>Type</th>
                  <th>Account</th>
                  <th>Age</th>
                  <th>Est. Cost</th>
                  <th>Attribution</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((r) => {
                  const key = resourceKey(r);
                  const deletable = DELETABLE_TYPES.has(r.resource_type);
                  const unattributed = isUnattributed(r);
                  return (
                    <tr key={key}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Select ${r.name || r.resource_id}`}
                          checked={selected.has(key)}
                          onChange={() => toggleSelectOne(r)}
                          disabled={!deletable}
                          title={deletable ? undefined : 'Delete not supported for this resource type - remove manually'}
                        />
                      </td>
                      <td>
                        <p style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>{r.name || r.resource_id.slice(0, 50)}</p>
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--slate-500)', fontFamily: 'monospace' }}>{r.region}</p>
                      </td>
                      <td><span className="badge badge-neutral" style={{ textTransform: 'none' }}>{r.resource_type.replace(/-/g, ' ')}</span></td>
                      <td><code style={{ fontSize: 'var(--text-xs)' }}>{r.account_id}</code></td>
                      <td style={{ fontSize: 'var(--text-xs)' }}>{formatRelativeTime(r.created_at || r.first_seen_at)}</td>
                      <td style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: (r.estimated_cost_usd_per_day || 0) > 0 ? 'var(--color-amber-dark)' : 'var(--slate-500)' }}>
                        {formatCostPerDay(r.estimated_cost_usd_per_day)}
                      </td>
                      <td>
                        {unattributed ? (
                          <span className="badge badge-warning">Unattributed</span>
                        ) : (
                          <span className={classNames('badge', 'badge-neutral')}>
                            {r.tags['lab-owner'] || r.tags['session-id'] || r.tags['managed-by']}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showConfirm && (
        <div className="modal-overlay" onClick={() => setShowConfirm(false)} role="dialog" aria-modal="true" aria-labelledby="cleanup-confirm-title">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <h2 id="cleanup-confirm-title" className="modal-title">Delete {selectedResources.length} resources?</h2>
              <button className="modal-close" onClick={() => setShowConfirm(false)} aria-label="Close">
                <X size={20} />
              </button>
            </header>
            <div className="modal-body">
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--slate-600)', marginBottom: 'var(--space-4)' }}>
                This permanently deletes the resources below from AWS. Only accounts with delete
                explicitly enabled will succeed; others will report why they were skipped.
              </p>
              <div className="table-container" style={{ maxHeight: 280, overflowY: 'auto' }}>
                <table className="table">
                  <thead><tr><th>Resource</th><th>Account</th><th>Est. Cost</th></tr></thead>
                  <tbody>
                    {selectedResources.map((r) => (
                      <tr key={resourceKey(r)}>
                        <td style={{ fontSize: 'var(--text-xs)' }}>{r.name || r.resource_id.slice(0, 40)}</td>
                        <td><code style={{ fontSize: 'var(--text-xs)' }}>{r.account_id}</code></td>
                        <td style={{ fontSize: 'var(--text-xs)' }}>{formatCostPerDay(r.estimated_cost_usd_per_day)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {selectedDailyCost > 0 && (
                <p style={{ marginTop: 'var(--space-4)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-primary-dark)' }}>
                  Estimated savings: ${selectedDailyCost.toFixed(2)}/day
                </p>
              )}
            </div>
            <footer className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowConfirm(false)} disabled={deleting}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? (<><Loader2 size={18} className="spin" /> Deleting...</>) : (<><Trash2 size={18} /> Delete {selectedResources.length} resources</>)}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
